import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Plus, Paperclip, Check, Sparkles, Archive, Link2, ArrowLeft, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { EmailThread, EmailMsg, AiAction, Contact, SystemRec, AccountListItem } from "@/lib/types";
import { ago, whenShort, shortDate, stageLabel, titleCase } from "@/lib/format";
import { Button, PageHeader, Loading, Empty, Tag, Pill, Dot, Avatar, Modal, Field, Menu, toast } from "@/components/ui";
import { RichEditor, type RichEditorHandle } from "@/components/RichEditor";
import { AttachPicker, AttachmentPills, toSendPayload, type OutAtt } from "./inbox/AttachPicker";
import { HtmlBody } from "./inbox/HtmlBody";
import { startWithSignature, withSignature } from "@/lib/signature";
import { useMe } from "@/app/MeContext";
import { SendControls, type SendOptions } from "./inbox/SendControls";
import { TemplatePicker } from "./inbox/TemplatePicker";
import { ComposeModal } from "./inbox/ComposeModal";

const FILTERS = [["all", "All"], ["customers", "Customers"], ["partners", "Partners"], ["flagged", "Flagged by AI"], ["unlinked", "Unlinked"], ["mine", "Assigned to me"], ["junk", "Junk"]] as const;
const CAT_LABEL: Record<string, { label: string; color: "red" | "yellow" | "accent" | "neutral" | "ai" }> = {
  system_down: { label: "System down", color: "red" },
  change_request: { label: "Change request", color: "yellow" },
  action_item: { label: "Action item", color: "accent" },
  meeting_invite: { label: "Added to calendar", color: "accent" },
  billing: { label: "Billing", color: "neutral" },
  partner: { label: "Partner", color: "neutral" },
  lead: { label: "Web lead", color: "accent" },
  other: { label: "Filed", color: "neutral" },
};

interface ThreadDetail extends EmailThread {
  inboxAddress: string | null; messages: EmailMsg[]; aiActions: AiAction[]; account: { id: string; name: string; number: string; stage: string } | null; contacts: Contact[]; systems: SystemRec[]; recentThreads: { id: string; subject: string; lastMessageAt: string }[]; partner: { id: string; name: string } | null;
}

export function InboxPage() {
  const { threadId } = useParams();
  const [params, setParams] = useSearchParams();
  const nav = useNavigate();
  const filter = params.get("filter") ?? "all";
  const inbox = params.get("inbox") ?? "";
  const q = params.get("q") ?? "";
  const [compose, setCompose] = useState(false);
  const key = `threads:${inbox}:${filter}:${q}`;
  const { data: threads, loading } = useQuery<EmailThread[]>(key, () => api.get<EmailThread[]>(`/inbox/threads?filter=${filter === "junk" ? "all" : filter}&status=${filter === "junk" ? "junk" : "open"}${inbox ? `&inbox=${inbox}` : ""}${q ? `&q=${encodeURIComponent(q)}` : ""}`), [filter, inbox, q]);
  const { data: inboxes } = useQuery<{ id: string; address: string }[]>("inboxes", () => api.get("/inbox/inboxes"));
  const setParam = (k: string, v: string) => { const p = new URLSearchParams(params); if (v) p.set(k, v); else p.delete(k); setParams(p); };
  const current = inboxes?.find((i) => i.id === inbox);
  const [search, setSearch] = useState(q);
  useEffect(() => { const t = setTimeout(() => { if (search !== q) setParam("q", search); }, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search]);

  return (
    <>
      <PageHeader title="Inbox" sub={current?.address ?? (inboxes && inboxes.length > 1 ? "All inboxes" : inboxes?.[0]?.address)}>
        <div className="chips desktop-only">
          {FILTERS.map(([k, l]) => <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setParam("filter", k === "all" ? "" : k)}>{l}</button>)}
        </div>
        <input className="input" style={{ width: 200 }} placeholder="Search mail" value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button variant="primary" onClick={() => setCompose(true)}><Plus size={14} /> Compose</Button>
      </PageHeader>
      <div className="chips mobile-only" style={{ padding: "8px 16px 0", display: threadId ? "none" : undefined }}>
        {FILTERS.map(([k, l]) => <button key={k} className={`chip ${filter === k ? "active" : ""}`} onClick={() => setParam("filter", k === "all" ? "" : k)}>{l}</button>)}
      </div>
      <div className={`inbox ${threadId ? "open" : ""}`}>
        <div className="thread-list">
          {loading && !threads ? <Loading /> : (threads ?? []).length === 0 ? <Empty>No threads{filter !== "all" ? " for this filter" : ""}.</Empty> : (threads ?? []).map((t) => (
            <div key={t.id} className={`thread-row ${t.id === threadId ? "active" : ""} ${t.unread ? "unread" : ""}`} onClick={() => nav(`/inbox/${t.id}${window.location.search}`)}>
              <div className="from">
                {t.unread && <span className="unread" />}
                <span className="truncate">{t.lastDirection === "outbound" ? `To ${t.participants[0] ?? ""}` : t.lastFromName}</span>
                <span className="time">{ago(t.lastMessageAt)}</span>
              </div>
              <div className="subject truncate">{t.subject || "(no subject)"}</div>
              <div className="tags">
                {t.aiCategories[0] && CAT_LABEL[t.aiCategories[0]] && <Tag color={CAT_LABEL[t.aiCategories[0]].color}>{CAT_LABEL[t.aiCategories[0]].label}</Tag>}
                {t.ownerName && <Tag color="neutral">Assigned: {t.ownerName.split(" ")[0]}</Tag>}
                {t.remindIfNoReplyAt && <Tag color="outline">Remind {shortDate(t.remindIfNoReplyAt)}</Tag>}
                <span className="truncate">{t.accountName ?? (t.partnerName ? `${t.partnerName} · Partner` : <span className="dim">Unlinked</span>)}</span>
              </div>
            </div>
          ))}
        </div>
        {threadId ? <ThreadView threadId={threadId} listKey={key} /> : (
          <div className="reading desktop-only"><Empty>Select a thread.</Empty></div>
        )}
      </div>
      {compose && <ComposeModal onClose={() => setCompose(false)} onSent={() => invalidate("threads")} />}
    </>
  );
}

function ThreadView({ threadId, listKey }: { threadId: string; listKey: string }) {
  const nav = useNavigate();
  const { data: t, loading, refetch } = useQuery<ThreadDetail>(`thread:${threadId}`, () => api.get<ThreadDetail>(`/inbox/threads/${threadId}`), [threadId]);
  const { data: inboxList } = useQuery<{ id: string; type: string; address: string }[]>("inboxes", () => api.get("/inbox/inboxes"));
  const inboxTypes = inboxList ? Object.fromEntries(inboxList.map((i) => [i.id, i.type])) : null;
  const me = useMe();
  const editor = useRef<RichEditorHandle>(null);
  const [busy, setBusy] = useState(false);
  const [atts, setAtts] = useState<OutAtt[]>([]);
  const [linkOpen, setLinkOpen] = useState(false);
  // Reply recipients. "all" is the default whenever the last message had anyone besides us and the sender.
  const [mode, setMode] = useState<"one" | "all" | null>(null);
  const [rcpt, setRcpt] = useState<{ to: string; cc: string } | null>(null);
  const [showQuoted, setShowQuoted] = useState<Record<string, boolean>>({});
  useEffect(() => { setMode(null); setRcpt(null); setAtts([]); }, [threadId]);
  useEffect(() => {
    if (t?.unread) { api.patch(`/inbox/threads/${t.id}`, { unread: false }).then(() => { invalidate(listKey); invalidate("badges"); }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t?.id, t?.unread]);
  if (loading && !t) return <div className="reading"><Loading /></div>;
  if (!t) return <div className="reading"><Empty>Thread not found.</Empty></div>;
  const last = [...t.messages].reverse().find((m) => m.direction === "inbound") ?? t.messages[t.messages.length - 1];
  const replyTo = last?.direction === "inbound" ? last.fromAddress : last?.toAddresses[0] ?? "";
  const replyToName = last?.direction === "inbound" ? last.fromName ?? last.fromAddress : last?.toAddresses[0] ?? "";
  const ours = new Set([t.inboxAddress, ...(inboxList ?? []).map((i) => i.address)].filter((x): x is string => !!x).map((x) => x.toLowerCase()));
  const others = last ? [...new Set([...last.toAddresses, ...last.ccAddresses].map((a) => a.toLowerCase()))].filter((a) => a !== replyTo.toLowerCase() && !ours.has(a)) : [];
  const effectiveMode: "one" | "all" = mode ?? (others.length ? "all" : "one");
  const recipients = rcpt ?? (effectiveMode === "all" ? { to: replyTo, cc: others.join(", ") } : { to: replyTo, cc: "" });
  const pickMode = (m: "one" | "all") => { setMode(m); setRcpt(m === "all" ? { to: replyTo, cc: others.join(", ") } : { to: replyTo, cc: "" }); };
  const splitAddrs = (s: string) => s.split(/[,;\s]+/).map((x) => x.trim()).filter(Boolean);
  const cats = t.aiCategories;
  const verified = last?.direction === "inbound" && last.senderVerified;
  const spfDkim = last?.direction === "inbound" ? `SPF ${last.spf ?? "?"} · DKIM ${last.dkim ?? "?"}` : "";

  const send = async (opts: SendOptions) => {
    const html = editor.current?.getHtml() ?? "";
    if (!html.replace(/<[^>]+>/g, "").trim()) { toast("Write a reply first", true); return; }
    setBusy(true);
    try {
      const to = splitAddrs(recipients.to), cc = splitAddrs(recipients.cc).filter((a) => !to.includes(a));
      if (!to.length) { toast("Add at least one recipient", true); return; }
      const r = await api.post<{ scheduled: boolean }>("/inbox/send", { threadId: t.id, to, cc, subject: t.subject.match(/^re:/i) ? t.subject : `Re: ${t.subject}`, html, ...toSendPayload(atts), scheduledAt: opts.scheduledAt ?? null, remindDays: opts.remindDays ?? null });
      editor.current?.setHtml(startWithSignature(me.signature));
      setAtts([]); setMode(null); setRcpt(null);
      toast(r.scheduled ? "Reply scheduled" : "Reply sent");
      refetch(); invalidate(listKey); invalidate("badges");
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  const undo = async (a: AiAction) => {
    try { await api.post(`/ai/actions/${a.id}/reverse`, {}); toast("Reversed"); refetch(); invalidate("badges"); invalidate(listKey); } catch (e) { toast((e as Error).message, true); }
  };
  const archive = async () => { await api.patch(`/inbox/threads/${t.id}`, { status: t.status === "archived" ? "open" : "archived" }); invalidate(listKey); nav("/inbox"); };
  const draftWithClaude = async () => {
    const instructions = prompt("Anything Claude should know for this reply? (optional)", "") ?? "";
    setBusy(true);
    try {
      const r = await api.post<{ html: string }>("/ai/draft-reply", { threadId: t.id, instructions });
      editor.current?.setHtml(withSignature(r.html, me.signature));
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  const sysDown = t.systems.find((s) => s.status === "red");
  const isShared = !!t.inboxAddress && !/^josh@/.test(t.inboxAddress) && (inboxTypes?.[t.inboxId] === "shared");
  const sysYellow = t.systems.find((s) => s.status === "yellow");

  return (
    <>
      <div className="reading">
        <div className="reading-head">
          <div className="row gap-8 mobile-only"><Link to={`/inbox${window.location.search}`} className="muted-2 row gap-4"><ArrowLeft size={14} /> Inbox</Link></div>
          <div className="row gap-8" style={{ alignItems: "flex-start" }}>
            <h2 className="grow">{t.subject || "(no subject)"}</h2>
            {isShared && <OwnerPicker thread={t} onChanged={() => { refetch(); invalidate(listKey); }} />}
            {t.status === "junk" ? (
              <Button size="md" variant="ghost" title="Not junk: restore and unblock the sender" onClick={async () => { await api.post(`/inbox/threads/${t.id}/not-junk`, {}); toast("Restored and unblocked"); invalidate("threads"); invalidate("badges"); nav("/inbox"); }}>Not junk</Button>
            ) : (
              <Menu trigger={<Button size="md" variant="ghost" title="Junk"><Ban size={13} /></Button>} items={[
                { label: `Junk: block ${replyTo}`, onClick: async () => { const r = await api.post<{ threadsHidden: number }>(`/inbox/threads/${t.id}/junk`, { scope: "email" }); toast(`Blocked sender, hid ${r.threadsHidden} thread${r.threadsHidden === 1 ? "" : "s"}`); invalidate("threads"); invalidate("badges"); nav("/inbox"); } },
                { label: `Junk: block @${replyTo.split("@")[1] ?? ""}`, danger: true, onClick: async () => { const r = await api.post<{ threadsHidden: number }>(`/inbox/threads/${t.id}/junk`, { scope: "domain" }); toast(`Blocked domain, hid ${r.threadsHidden} thread${r.threadsHidden === 1 ? "" : "s"}`); invalidate("threads"); invalidate("badges"); nav("/inbox"); } },
              ]} />
            )}
            <Button size="md" variant="ghost" title={t.status === "archived" ? "Unarchive" : "Archive"} onClick={archive}><Archive size={13} /></Button>
            <Button size="md" variant="ghost" title="Link to account or partner" onClick={() => setLinkOpen(true)}><Link2 size={13} /></Button>
          </div>
          <div className="row wrap gap-8">
            {cats.map((c) => CAT_LABEL[c] && <Pill key={c} color={CAT_LABEL[c].color === "red" ? "red" : CAT_LABEL[c].color === "yellow" ? "yellow" : undefined}>{CAT_LABEL[c].color === "red" && <Dot color="red" />}{CAT_LABEL[c].label}</Pill>)}
            {last?.direction === "inbound" && <Pill>{verified ? "Verified sender · SPF and DKIM pass" : last.spf === "unknown" || !last.spf ? "Sender not verified · no auth results" : `Sender ${last.senderVerified ? "verified" : "not verified"} · ${spfDkim}`}</Pill>}
            {t.account ? <Link to={`/accounts/${t.account.id}`} className="pill accent">{t.account.name} · {t.account.number}</Link> : t.partner ? <Link to={`/partners/${t.partner.id}`} className="pill accent">{t.partner.name} · Partner</Link> : <button className="pill" onClick={() => setLinkOpen(true)}>Unlinked · link to account or partner</button>}
          </div>
        </div>
        <div className="messages">
          {t.messages.map((m) => {
            const body = m.htmlBody ?? null;
            const text = m.textBody ?? "";
            const [main, quoted] = splitQuoted(text);
            return (
              <div key={m.id} className="message">
                <div className="mhead">
                  <Avatar name={m.fromName ?? m.fromAddress} />
                  <div className="col gap-2 grow">
                    <div className="row gap-8"><span style={{ fontWeight: 500 }}>{m.fromName ?? m.fromAddress}</span><span className="muted small">{m.fromAddress}</span>{m.status === "scheduled" && <Tag color="outline">Scheduled {whenShort(m.scheduledAt)}</Tag>}{m.status === "bounced" && <Tag color="red">Bounced</Tag>}</div>
                    <span className="muted small">to {m.toAddresses.join(", ")}{m.ccAddresses.length ? ` · cc ${m.ccAddresses.join(", ")}` : ""} · {whenShort(m.sentAt)}</span>
                  </div>
                  {m.status === "scheduled" && <button className="btn-link small" onClick={async () => { await api.post(`/inbox/messages/${m.id}/cancel`, {}); refetch(); }}>Cancel send</button>}
                </div>
                {body && m.direction === "inbound" ? <HtmlBody html={body} /> : body ? <div className="body html" dangerouslySetInnerHTML={{ __html: body }} /> : (
                  <div className="body">{main}{quoted && (showQuoted[m.id] ? <div className="muted" style={{ marginTop: 8 }}>{quoted}</div> : <div><button className="btn-link small muted" onClick={() => setShowQuoted((s) => ({ ...s, [m.id]: true }))}>···</button></div>)}</div>
                )}
                {m.attachments.length > 0 && (
                  <div className="row wrap gap-6 mt-8">
                    {m.attachments.map((a) => <a key={a.id} className="pill" href={`/api/inbox/messages/${m.id}/attachments/${a.id}`} target="_blank" rel="noreferrer"><Paperclip size={11} /> {a.filename} <span className="muted xs">{(a.size / 1024).toFixed(0)} KB</span></a>)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="reply-box">
          <div className="to row gap-8 wrap" style={{ alignItems: "center" }}>
            <span>{effectiveMode === "all" ? `Reply all` : `Reply to ${replyToName}`}</span>
            <span className="seg" style={{ marginLeft: "auto" }}>
              <button className={effectiveMode === "one" ? "active" : ""} onClick={() => pickMode("one")}>Reply</button>
              <button className={effectiveMode === "all" ? "active" : ""} onClick={() => pickMode("all")} title={others.length ? `Also: ${others.join(", ")}` : "No one else on the last message"}>Reply all{others.length ? ` (${others.length + 1})` : ""}</button>
            </span>
          </div>
          <div className="addr">
            <label>To</label><input className="input" value={recipients.to} onChange={(e) => setRcpt({ ...recipients, to: e.target.value })} />
            {(effectiveMode === "all" || recipients.cc) && <><label>Cc</label><input className="input" placeholder="name@company.com, other@company.com" value={recipients.cc} onChange={(e) => setRcpt({ ...recipients, cc: e.target.value })} /></>}
          </div>
          <RichEditor key={t.id} ref={editor} placeholder="Write a reply" initialHtml={startWithSignature(me.signature)} />
          <AttachmentPills value={atts} onChange={setAtts} />
          <div className="tools">
            <Button size="md" variant="ai" disabled={busy} onClick={draftWithClaude}><Sparkles size={13} /> Draft with Claude</Button>
            <TemplatePicker accountId={t.account?.id} contactName={t.contacts.find((c) => c.email === replyTo)?.name ?? replyToName} onPick={(tpl) => editor.current?.setHtml(withSignature(tpl.body, me.signature))} />
            <AttachPicker value={atts} onChange={setAtts} accountId={t.account?.id} partnerId={t.partner?.id} />
            <SendControls busy={busy} onSend={send} />
          </div>
        </div>
      </div>
      <aside className="context">
        {t.account && (
          <div className={`card pad ${sysDown ? "red" : ""}`}>
            <div className="row gap-10">
              <Avatar name={t.account.name} large />
              <div className="col gap-2">
                <Link to={`/accounts/${t.account.id}`} style={{ color: "var(--text)", fontWeight: 600 }}>{t.account.name}</Link>
                <span className="mono xs muted">{t.account.number} · {stageLabel(t.account.stage)}</span>
              </div>
            </div>
            {(sysDown || sysYellow) && <div className="row gap-6 mt-8 small" style={{ color: sysDown ? "var(--status-red-text)" : "var(--status-yellow-text)" }}><Dot color={sysDown ? "red" : "yellow"} pulse={!!sysDown} />{(sysDown ?? sysYellow)!.name} {sysDown ? "is down" : "has an open change request"}</div>}
          </div>
        )}
        {t.aiActions.length > 0 && (
          <div>
            <h3>What Claude did with this email</h3>
            {t.aiActions.map((a) => (
              <div key={a.id} className="ai-action">
                <Check size={14} className="check" style={a.outcome !== "applied" ? { opacity: 0.4 } : undefined} />
                <span className="text" style={a.outcome !== "applied" ? { textDecoration: "line-through", color: "var(--text-3)" } : undefined}>{a.summary}</span>
                {a.outcome === "applied" && a.actionType !== "classify" && a.actionType !== "send_sms" && <Button size="sm" onClick={() => undo(a)}>Undo</Button>}
                {a.outcome === "reversed" && <span className="xs muted">Undone</span>}
              </div>
            ))}
          </div>
        )}
        {t.contacts.length > 0 && (
          <div>
            <h3>Contacts</h3>
            {t.contacts.map((c) => <div key={c.id} className="col gap-2 mb-8"><span>{c.name}{c.isPrimary && <span className="muted"> · Primary</span>}</span><span className="small muted">{[c.title, titleCase(c.role)].filter(Boolean).join(" · ")}</span></div>)}
          </div>
        )}
        {t.recentThreads.length > 0 && (
          <div>
            <h3>Recent threads</h3>
            {t.recentThreads.map((r) => <div key={r.id} className="mb-8"><Link to={`/inbox/${r.id}`} style={{ color: "var(--text-body)" }}>{r.subject || "(no subject)"}</Link> <span className="small muted">· {shortDate(r.lastMessageAt)}</span></div>)}
          </div>
        )}
      </aside>
      {linkOpen && <LinkModal thread={t} onClose={() => setLinkOpen(false)} onLinked={() => { setLinkOpen(false); refetch(); invalidate(listKey); }} />}
    </>
  );
}

function OwnerPicker({ thread, onChanged }: { thread: ThreadDetail; onChanged: () => void }) {
  const { data: users } = useQuery<{ id: string; name: string }[]>("users-brief", () => api.get<{ id: string; name: string }[]>("/me/users"));
  return (
    <select className="select" style={{ width: "auto", height: 28, fontSize: 12 }} value={thread.ownerUserId ?? ""} title="Thread owner (shared inbox)" onChange={async (e) => { await api.patch(`/inbox/threads/${thread.id}`, { ownerUserId: e.target.value || null }); onChanged(); }}>
      <option value="">Unassigned</option>
      {(users ?? []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
    </select>
  );
}

function splitQuoted(text: string): [string, string | null] {
  const idx = text.search(/\n(On .+wrote:|-----Original Message-----|From: .+\n(Sent|Date):)/);
  if (idx < 0) return [text, null];
  return [text.slice(0, idx).trimEnd(), text.slice(idx).trim()];
}

function LinkModal({ thread, onClose, onLinked }: { thread: ThreadDetail; onClose: () => void; onLinked: () => void }) {
  const { data: accounts } = useQuery<AccountListItem[]>("accounts", () => api.get<AccountListItem[]>("/accounts"));
  const { data: partners } = useQuery<{ id: string; name: string; type: string }[]>("partners-brief", () => api.get<{ id: string; name: string; type: string }[]>("/partners"));
  const [kind, setKind] = useState<"account" | "partner">(thread.partner && !thread.account ? "partner" : "account");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(thread.accountId ?? thread.partner?.id ?? "");
  const list = useMemo(() => (accounts ?? []).filter((a) => !q || a.name.toLowerCase().includes(q.toLowerCase())).slice(0, 50), [accounts, q]);
  const plist = useMemo(() => (partners ?? []).filter((p) => !q || p.name.toLowerCase().includes(q.toLowerCase())).slice(0, 50), [partners, q]);
  const save = async () => {
    try {
      await api.patch(`/inbox/threads/${thread.id}`, kind === "account" ? { accountId: sel || null, partnerId: null } : { partnerId: sel || null, accountId: null });
      onLinked();
    } catch (e) { toast((e as Error).message, true); }
  };
  return (
    <Modal title="Link thread" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" onClick={save}>Save</Button></>}>
      <div className="row gap-8 mb-8">
        <div className="seg">
          <button className={kind === "account" ? "active" : ""} onClick={() => { setKind("account"); setSel(thread.accountId ?? ""); }}>Account</button>
          <button className={kind === "partner" ? "active" : ""} onClick={() => { setKind("partner"); setSel(thread.partner?.id ?? ""); }}>Partner</button>
        </div>
      </div>
      <Field label={kind === "account" ? "Find account" : "Find partner"}><input className="input" autoFocus value={q} onChange={(e) => setQ(e.target.value)} /></Field>
      <div className="col gap-2" style={{ maxHeight: 300, overflowY: "auto" }}>
        <label className="row gap-8" style={{ minHeight: 30 }}><input type="radio" checked={sel === ""} onChange={() => setSel("")} /> <span className="muted">Unlinked</span></label>
        {kind === "account"
          ? list.map((a) => <label key={a.id} className="row gap-8" style={{ minHeight: 30 }}><input type="radio" checked={sel === a.id} onChange={() => setSel(a.id)} /> {a.name} <span className="mono xs muted">{a.number}</span></label>)
          : plist.map((p) => <label key={p.id} className="row gap-8" style={{ minHeight: 30 }}><input type="radio" checked={sel === p.id} onChange={() => setSel(p.id)} /> {p.name} <span className="xs muted">{p.type.replace("_", " ")}</span></label>)}
        {kind === "partner" && plist.length === 0 && <div className="small muted">No partners yet. Add one under Partners.</div>}
      </div>
    </Modal>
  );
}
